import React, { useEffect, useState } from 'react';
import {
  FaThumbtack,
  FaClone,
  FaProjectDiagram,
  FaQuestionCircle,
  FaRobot,
  FaBookOpen, 
  FaArrowUp,
  FaQuoteLeft,
} from "react-icons/fa";
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Typewriter from 'typewriter-effect';
import Lottie from 'lottie-react';
import atomsAnimation2 from '../assets/Animation_1736003383425.json';
import { Link } from 'react-router-dom';
import axios from 'axios';

export default function Homepage() {

  const [subjects, setSubjects] = useState([]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };
  
  const pageTransition = {
    type: "tween",
    duration: 0.4,
  };

  useEffect(() => {
    axios.get('https://backend.scholarkart.in/api/subjects/on-sale')
      .then(response => {
        setSubjects(response.data);
      })
      .catch(error => {
        console.error('Error fetching subjects:', error);
      });
  }, []);


const features = [
  {
    title: "Lesson Notes",
    icon: <FaBookOpen />,
    desc: "Clear, concise explanations of every topic - crafted for easy understanding.",
    slug: "lesson-notes",
  },
  {
    title: "Takeaways",
    icon: <FaThumbtack />,
    desc: "Key points and summaries to reinforce what matters most.",
    slug: "take-aways",
  },
  {
    title: "Flash Cards",
    icon: <FaClone />,
    desc: "Interactive cards to test and strengthen memory through active recall.",
    slug: "flash-cards",
  },
  {
    title: "Mind Maps",
    icon: <FaProjectDiagram />,
    desc: "Visual diagrams that connect ideas and boost conceptual clarity.",
    slug: "mind-maps",
  },
  {
    title: "Quizzes",
    icon: <FaQuestionCircle />,
    desc: "Smart assessments that adapt to your performance and guide your revision.",
    slug: "quizzess",
  },
  {
    title: "AI Chatbot",
    icon: <FaRobot />,
    desc: "Your instant study buddy that answers only from your prescribed textbooks - no distractions, just relevant clarity.",
    slug: "ai-chatbot",
  },
];




  return (
    <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
  >
<>
    <Helmet>
      <title>SvaLearn - AI Powered eLearning Platform</title>
      <meta name="description" content="Sva Learn is an AI-powered eLearning platform designed to revolutionize the way students absorb and retain knowledge. With a focus on chapter-wise mastery, it offers a structured, intuitive, and engaging learning experience tailored to individual needs." />
    </Helmet>

    <div className="min-h-screen flex flex-col">
      {/* Hero Section */}
      <section id="home" className="bg-gradient-to-r from-[#264790] via-[#1B3268] to-[#1B3268] pt-10 md:pt-0 pb-0 md:pb-0 px-6">




        
        <div className="container mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* Left Text Column */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-[white] mb-4 leading-snug" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>
              <Typewriter
                options={{
                  strings: [
                    'Lesson Notes to Mind Maps - All in One',
                    'Where Learning Meets Possibility!',
                    'Learn Your Way. Succeed Your Way.'
                  ],
                  autoStart: true,
                  loop: true,
                  delay: 50,
                  deleteSpeed: 30,
                }}
              />
            </h2>
            <p className="text-lg text-yellow-400 mb-6">
              SvaLearn uses intelligent algorithms to personalize content delivery, track progress, and recommend the most effective learning path for each student.
            </p>
            <a
              href="/subjects"
              className="inline-block bg-[#ff7a00] hover:bg-[#e06a00] text-white px-6 py-3 rounded-lg font-medium transition shadow-lg hover:scale-105"
            >
              Explore Courses
            </a>
          </motion.div>

          {/* Right Image Column */}
              <Lottie
                animationData={atomsAnimation2}
                loop={true}
                style={{
                  width: '75%',
                  height: '75%',
                  objectFit: 'cover',
                }}
              />
        </div>
      </section>

    <section id="subjects" className="py-10 bg-white dark:bg-gray-900 relative">
      
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-center text-[#264790] mb-12" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>
          Popular eLearning Subjects
        </h2>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {subjects.map((subject, idx) => (
            <div
              key={idx}
              className="bg-white dark:bg-gray-700 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition duration-300"
            >
              <img
                src={subject.coverImage }
                alt={subject.subjectName}
                className="w-full h-30 object-cover"
              />
              <div className="p-6">
                <span className="inline-block bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-full mb-2">
                  ₹{subject.price} one-time
                </span>
                {subject.courseIds && subject.courseIds.length > 0 && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {subject.courseIds.map((course, index) => (
                      <div key={index}>
                        {course.courseName}
                      </div>
                    ))}
                  </div>
                )}
                <h4 className="text-xl font-bold text-[#012f6e] dark:text-white mb-1">
                  {subject.subjectName}
                </h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  {subject.subjectOutcome?.slice(0, 100)}...
                </p>
                {subject.courseIds && subject.courseIds.length > 0 && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {subject.courseIds.map((course, index) => (
                      <div key={index}>
                        {course.affiliation}
                      </div>
                    ))}
                  </div>
                )}
                <Link to={`/subjects_details/${subject._id}`} className="block w-full">
                  <button className="w-10/12 mt-6 bg-[#ff7a00] text-white py-2 rounded-full hover:bg-orange-600 transition">
                    Explore Module
                  </button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>


      {/* Features Snippet */}
      <section id="features" className="bg-white py-10 px-6">
        <div className="container mx-auto">
        <h2 className="text-3xl font-bold text-center text-[#264790] mb-12" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>
          Smart Tools for Smarter Learning
        </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="bg-[#f9fafb] p-6 rounded-xl shadow transition-all duration-300 ease-in-out hover:-translate-y-1 hover:bg-[#fef8f3] hover:shadow-[0_8px_30px_rgba(255,122,0,0.15)] cursor-pointer"
            >
              <div className="text-4xl mb-4 text-[#ff7a00]">{feature.icon}</div>
              <h4 className="text-xl font-semibold text-[#264790] mb-2">{feature.title}</h4>
              <p className="text-gray-600">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
        </div>
      </section>




    </div>
    </>
    </motion.div>
  );
}
